import { useState } from "react";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { RhythmGrid, RhythmCell } from "@/components/RhythmGrid";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

type Pattern = "lmhm" | "hmlm" | "hlmh" | "sixeight" | "broken";

// Em chord = B-E-G — Low B, Middle E, High G
const lmhm: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["E2"], caption: "Em bass" },
  { hand: "R", on: true, pitches: ["B3"], caption: "L · B" },
  { hand: "R", on: true, pitches: ["E4"], caption: "M · E" },
  { hand: "R", on: true, pitches: ["G4"], caption: "H · G" },
  { hand: "R", on: true, pitches: ["E4"], caption: "M · E" },
];

const hmlm: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["E2"], caption: "Em bass" },
  { hand: "R", on: true, pitches: ["G4"], caption: "H · G" },
  { hand: "R", on: true, pitches: ["E4"], caption: "M · E" },
  { hand: "R", on: true, pitches: ["B3"], caption: "L · B" },
  { hand: "R", on: true, pitches: ["E4"], caption: "M · E" },
];

const hlmh: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["E2"], caption: "Em bass" },
  { hand: "R", on: true, pitches: ["G4"], caption: "H · G" },
  { hand: "R", on: true, pitches: ["B3"], caption: "L · B" },
  { hand: "R", on: true, pitches: ["E4"], caption: "M · E" },
  { hand: "R", on: true, pitches: ["G4"], caption: "H · G" },
];

// 6/8: 3 notes up + octave-of-first + 2 notes down = 6 notes per bar
const sixeight: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["E2"], caption: "1" },
  { hand: "R", on: true, pitches: ["B3"], caption: "B" },
  { hand: "R", on: true, pitches: ["E4"], caption: "E" },
  { hand: "R", on: true, pitches: ["G4"], caption: "G" },
  { hand: "R", on: true, pitches: ["B4"], caption: "B↑" },
  { hand: "R", on: true, pitches: ["G4"], caption: "G" },
  { hand: "R", on: true, pitches: ["E4"], caption: "E" },
];

// Broken chord — bottom alone, then top two together
const broken: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["E2"], caption: "Em bass" },
  { hand: "R", on: true, pitches: ["B3"], caption: "Low" },
  { hand: "R", on: true, pitches: ["E4", "G4"], caption: "Top 2" },
  { hand: "R", on: true, pitches: ["B3"], caption: "Low" },
  { hand: "R", on: true, pitches: ["E4", "G4"], caption: "Top 2" },
];

export default function PianoFoundation16() {
  const { lesson, course, accent } = findLesson("piano-foundation", "16");
  const [pattern, setPattern] = useState<Pattern>("lmhm");
  const grid =
    pattern === "lmhm" ? lmhm :
    pattern === "hmlm" ? hmlm :
    pattern === "hlmh" ? hlmh :
    pattern === "sixeight" ? sixeight : broken;

  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>An <strong>arpeggio</strong> = the same chord but played one note at a time. Same four chords from Lesson 15 — Em, C, G, D — broken into single notes.</Highlight>
        <Highlight>Three core patterns: <strong>L-M-H-M</strong>, <strong>H-M-L-M</strong>, and <strong>H-L-M-H</strong> — the most useful pattern. Each chord plays twice for a nice count of four.</Highlight>
        <Highlight>Hold the first note. Don't release the bottom of the chord between repetitions — keep the thumb (or pinky) anchored.</Highlight>
        <Highlight><strong>6/8 feel</strong>: three notes up + the first note an octave higher + two notes down. The signature pattern of ballad accompaniment.</Highlight>
        <Highlight>Dynamics through <strong>breathing</strong> — breathe in, the body tenses and you naturally play louder; breathe out, the body relaxes and you play softer. Don't even think about it.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="What an arpeggio is" title="Same chord — one note at a time" accent={accent.hex}>
        <p>
          You already know how to hit the chord all at once. The arpeggio takes those same
          three notes and rolls them out one by one. The harmony is identical; the texture
          transforms. Listen to almost any acoustic ballad — the right hand is rarely smashing
          chords. It's almost always arpeggiating.
        </p>

        <KeyIdea>
          With arpeggios, the chord doesn't change — only the order in which the notes come
          out does. The four chords from the previous lesson (Em, C, G, D in G major) stay
          the same; we just deliver them differently.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The three core patterns" title="L-M-H-M · H-M-L-M · H-L-M-H" accent={accent.hex}>
        <p>
          For each chord (let's use <strong>Em = B-E-G</strong> as the example), the three
          notes are Low (B), Middle (E), High (G). the three patterns shuffle that
          order:
        </p>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button onClick={() => setPattern("lmhm")} className={pattern === "lmhm" ? "nsm-btn-primary" : "nsm-btn-ghost"}>L-M-H-M</button>
          <button onClick={() => setPattern("hmlm")} className={pattern === "hmlm" ? "nsm-btn-primary" : "nsm-btn-ghost"}>H-M-L-M</button>
          <button onClick={() => setPattern("hlmh")} className={pattern === "hlmh" ? "nsm-btn-primary" : "nsm-btn-ghost"}>H-L-M-H</button>
          <button onClick={() => setPattern("sixeight")} className={pattern === "sixeight" ? "nsm-btn-primary" : "nsm-btn-ghost"}>6/8 feel</button>
          <button onClick={() => setPattern("broken")} className={pattern === "broken" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Broken chord</button>
        </div>

        <RhythmGrid
          cells={grid}
          subdivisions={1}
          beats={pattern === "sixeight" ? 6 : pattern === "broken" ? 5 : 5}
          bpm={pattern === "sixeight" ? 100 : 88}
          label={
            pattern === "lmhm" ? "L-M-H-M · low-middle-high-middle (rises and dips)" :
            pattern === "hmlm" ? "H-M-L-M · high-middle-low-middle (descends and lifts)" :
            pattern === "hlmh" ? "H-L-M-H · high-low-middle-high (the most useful pattern)" :
            pattern === "sixeight" ? "6/8 feel · 3 up + octave + 2 down (ballad classic)" :
            "Broken chord · bottom note alone, then top two together"
          }
        />

        <Tip>
          When you play arpeggios, hold the first note. The thumb (or pinky) stays anchored
          on the bottom of the chord between repetitions — releasing it makes the loop sound
          jumpy. Press it once at the start, then let the other fingers do the work; press
          it again only at the next chord change.
        </Tip>
      </Insight>

      <Insight eyebrow="The 6/8 feel" title="The pattern behind every great ballad" accent={accent.hex}>
        <p>
          Many ballads aren't in 4/4 — they're in 6/8: six eighth notes per bar, felt as two
          big beats of three. That's where the rocking, triplet lilt comes from.
          For 6/8, take the chord notes (B, E, G), play them up, add the first note an octave
          above, then come back down through the middle two:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          B · E · G · B↑ · G · E   (six notes per bar)
        </p>

        <Pull>
          This style will be used to play all of those incredible ballad songs out there.
        </Pull>

        <p>
          The 6/8 lift between the third and fourth notes (G up to B↑) is what gives the
          pattern its lilt. It's the rocking-cradle feeling at the heart of slow ballad piano.
        </p>
      </Insight>

      <Insight eyebrow="Broken chords" title="Halfway between block and arpeggio" accent={accent.hex}>
        <p>
          Broken chords are arpeggios that don't fully break apart. Take the chord and split
          it into two halves: <strong>the bottom note alone</strong>, then <strong>the top
          two notes together</strong>. Loop that.
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          B (alone) · E + G (together) · B (alone) · E + G (together)
        </p>

        <p>
          That's the sound of Aerosmith's Dream On, The Beatles' Let It Be and John Lennon's
          Imagine — all great songs built on broken chords.
        </p>
      </Insight>

      <Insight eyebrow="Dynamics" title="Breathe in for loud, breathe out for soft" accent={accent.hex}>
        <p>
          The piano can do exactly two things: play softer or play louder. Both are produced
          through volume control — what we call <strong>dynamics</strong>. With arpeggios
          especially, dynamics are everything.
        </p>

        <Pull>
          Whenever I breathe in I tend to play louder and whenever I breathe out I tend
          to play softer because inevitably breathing out relaxes the body and breathing
          in is a bit tense. Right? So breathing in I didn't do anything. I just breathe
          in and I just let the body do the job and play loud itself.
        </Pull>

        <p>
          The trick is that breathing in tenses the torso slightly; breathing out relaxes it.
          That tension translates straight to the fingertips — your hand naturally strikes
          harder on the inhale and softer on the exhale. Keep the breathing natural, not
          exaggerated, and you'll get a long, sweeping volume curve across each phrase
          without consciously thinking about it.
        </p>

        <KeyIdea>
          Music has three dimensions, not two. X-axis is time. Y-axis is pitch. Z-axis is
          dynamics. Most beginners only play in two dimensions; arpeggios are where the
          third one becomes audible.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Practice path" title="Hold the chord progression · cycle the patterns" accent={accent.hex}>
        <Steps>
          <Step>
            Pick the four-chord progression from Lesson 15: Em → C → G → D. One bar per
            chord.
          </Step>
          <Step>
            Apply <strong>L-M-H-M</strong> over the loop. Each chord gets four notes
            arpeggiated. Loop until it feels effortless.
          </Step>
          <Step>
            Switch to <strong>H-M-L-M</strong>. Same four chords, descending shape.
          </Step>
          <Step>
            Switch to <strong>H-L-M-H</strong> — the most useful pattern. The bottom note is
            "scooped" between the two highs. It feels different from the other two.
          </Step>
          <Step>
            For ballads, switch to the <strong>6/8 feel</strong> — six notes per bar. For
            songs that want a thinner, simpler texture, drop to <strong>broken chords</strong>.
          </Step>
          <Step>
            Add the breathing — inhale through the rising part of each phrase, exhale through
            the falling part. The dynamics will appear by themselves.
          </Step>
        </Steps>

        <Tip>
          Keep arpeggios slow but not too slow — be careful not to playing them so slow
          that they start sounding like a melody. They're an accompaniment pattern; there
          should be a singer or an instrument floating on top. Leave room for that voice.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-17"
        label="Lesson 17 · Percussive Grooving & following your Bandmates"
        why="Arpeggios are flowing. The next lesson swings the other direction — percussive piano grooves that borrow from drummers, guitarists, and the body itself."
      />
    </LessonShell>
  );
}
